// src/auth/SessionProvider.jsx
import React from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

const SessionContext = React.createContext({
  initializing: true,
  signedIn: false,
  user: null,
});

export function SessionProvider({ children }) {
  const [initializing, setInitializing] = React.useState(true);
  const [signedIn, setSignedIn] = React.useState(false);
  const [user, setUser] = React.useState(null);

  const refreshSession = React.useCallback(async () => {
    try {
      // This triggers the Hosted UI code-exchange if we were just redirected back
      const session = await fetchAuthSession();
      const hasTokens =
        !!session?.tokens?.idToken || !!session?.tokens?.accessToken;
      if (hasTokens) {
        try {
          const u = await getCurrentUser();
          setUser(u || null);
        } catch {
          setUser(null);
        }
        setSignedIn(true);
      } else {
        setSignedIn(false);
        setUser(null);
      }
    } catch {
      // No session (signed out or not completed)
      setSignedIn(false);
      setUser(null);
    } finally {
      setInitializing(false);
    }
  }, []);

  React.useEffect(() => {
    // On first load and after any Hosted UI redirect, do the exchange
    refreshSession();
  }, [refreshSession]);

  React.useEffect(() => {
    // Keep in sync with Amplify Auth Hub events
    const sub = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
        case 'cognitoHostedUI':
          refreshSession();
          break;
        case 'signedOut':
        case 'signOut':
          setSignedIn(false);
          setUser(null);
          break;
        default:
          break;
      }
    });
    return () => sub();
  }, [refreshSession]);

  const value = React.useMemo(
    () => ({ initializing, signedIn, user }),
    [initializing, signedIn, user]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return React.useContext(SessionContext);
}
